import React, { useState } from 'react';
import RetroButton from '../components/ui/RetroButton';
import RetroBadge from '../components/ui/RetroBadge';
import SafeIcon from '../common/SafeIcon';
import ReserveModal from './Reserve/ReserveModal';
import MailerCard from './Reserve/MailerCard';

const Sterling = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [activeSide, setActiveSide] = useState('front');
  const [reservedSlots, setReservedSlots] = useState({});

  // Sterling Heights specific data
  const sterlingData = {
    id: 'sterling-heights',
    name: 'Sterling Heights',
    state: 'Michigan',
    zipCodes: '48310, 48312, 48313'
  };

  const handleOpenModal = (slotDetails) => {
    setSelectedSlot({
      ...slotDetails,
      cityName: 'Sterling Heights'
    });
    setShowModal(true);
  };

  const handlePledgeSubmit = (slot) => {
    setReservedSlots(prev => ({ ...prev, [slot.id]: true }));
    setShowModal(false);
    alert('Your reservation request has been sent! Our team will contact you shortly.');
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen font-body text-slate-900">
      <h1 className="sr-only">Sterling Heights EDDM Mailer | Reach 15k Homes | MailboxFlyer</h1>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-900 text-white border-b-8 border-emerald-500">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-emerald-500/10 skew-x-12 transform translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1 text-center lg:text-left">
              <RetroBadge 
                text="Sterling Heights: Oct 15th Mailing" 
                color="bg-orange-500 text-white" 
                rotation="-rotate-2" 
                className="mb-8 border-white"
              />
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter uppercase mb-8 drop-shadow-[4px_4px_0_rgba(16,185,129,1)]">
                Put Your Business in <span className="text-emerald-400">15,000</span> Sterling Heights Mailboxes.
              </h2>
              <p className="font-display text-2xl md:text-3xl uppercase text-slate-300 mb-8 max-w-2xl">
                For less than <span className="text-white underline decoration-orange-500 decoration-4 underline-offset-4">5 Cents Per Home.</span>
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <a href="#availability">
                  <RetroButton 
                    variant="primary" 
                    className="!bg-emerald-500 !text-white !border-white !px-10"
                  >
                    View Open Slots
                  </RetroButton>
                </a>
              </div>
            </div>

            {/* Postcard Preview */}
            <div className="flex-1 w-full max-w-xl">
              <div className="bg-white border-4 border-slate-900 rounded-[2rem] p-4 shadow-[12px_12px_0px_0px_rgba(16,185,129,1)] relative overflow-hidden rotate-2">
                <div className="aspect-[12/9] bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center p-8 text-center">
                  <SafeIcon name="Mail" className="text-slate-300 text-6xl mb-4" />
                  <p className="font-display text-2xl text-slate-900 uppercase">9" x 12" Full Color</p>
                  <p className="font-body font-bold text-slate-400">Arriving in Sterling Heights Oct 15th</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mental Model Section */}
      <section className="py-24 bg-white border-b-4 border-slate-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mb-8 text-slate-900">
            How is this so cheap? <br/>
            <span className="text-emerald-600">(It's a group rate).</span>
          </h2>
          <div className="bg-slate-50 border-4 border-slate-900 p-8 md:p-12 rounded-[2.5rem] shadow-retro-sm">
            <p className="font-body text-xl md:text-2xl font-medium text-slate-700 leading-relaxed">
              "Instead of paying <span className="text-red-500 line-through font-bold">$8,000</span> to mail by yourself, we split the cost between 16 local businesses. You pay for a slice of the postcard, but <span className="text-slate-900 font-bold underline decoration-emerald-500 decoration-4">you reach all 15,000 homes."</span>
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Availability Grid */}
      <section id="availability" className="py-24 bg-slate-50 border-b-4 border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <RetroBadge text="Live Availability" color="bg-emerald-500 text-white" className="mb-4" />
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter mb-4 text-slate-900">Claim Your Spot</h2>
            <p className="font-body text-xl font-bold text-slate-500 max-w-2xl mx-auto">
              Select an open slot on the front or back of the card to reserve your category for the Sterling Heights {sterlingData.zipCodes} edition.
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-3 bg-white p-2 rounded-full border-4 border-slate-900 shadow-retro-sm">
              <button 
                onClick={() => setActiveSide('front')} 
                className={`px-8 py-2 rounded-full font-display uppercase tracking-widest text-sm transition-all ${activeSide === 'front' ? 'bg-emerald-500 text-white border-2 border-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-900'}`}
              >
                Front Side
              </button>
              <button 
                onClick={() => setActiveSide('back')} 
                className={`px-8 py-2 rounded-full font-display uppercase tracking-widest text-sm transition-all ${activeSide === 'back' ? 'bg-emerald-500 text-white border-2 border-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-900'}`}
              >
                Back Side
              </button>
            </div>
          </div>

          <MailerCard 
            city={sterlingData} 
            side={activeSide} 
            reservedSlots={reservedSlots} 
            onSlotClick={handleOpenModal} 
          />
        </div>
      </section>

      {/* Pricing Information */}
      <section className="py-24 bg-white border-b-4 border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-slate-50 border-2 border-slate-200 p-8 rounded-2xl">
              <h3 className="font-display text-xl uppercase mb-2">Small Slot</h3>
              <p className="font-display text-4xl text-emerald-600 mb-4">$450</p>
              <p className="font-body text-sm font-bold text-slate-500">Perfect for coupons and logo awareness. 2" x 3.5"</p>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-200 p-8 rounded-2xl relative">
              <div className="absolute -top-3 right-4 bg-orange-500 text-white text-[10px] uppercase px-3 py-1 rounded-full font-black">Popular</div>
              <h3 className="font-display text-xl uppercase mb-2">Medium Slot</h3>
              <p className="font-display text-4xl text-emerald-600 mb-4">$600</p>
              <p className="font-body text-sm font-bold text-slate-500">Best for menus and service lists. 4" x 3.5"</p>
            </div>
            <div className="bg-slate-50 border-2 border-slate-200 p-8 rounded-2xl">
              <h3 className="font-display text-xl uppercase mb-2">Large Slot</h3>
              <p className="font-display text-4xl text-emerald-600 mb-4">$800</p>
              <p className="font-body text-sm font-bold text-slate-500">Maximum visibility and authority. 7" x 5"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifics Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mb-8 text-emerald-400">
                Mailing to Your <br/>Actual Customers
              </h2>
              <div className="space-y-6">
                {[
                  { icon: 'Map', title: 'Target Zip Codes', text: sterlingData.zipCodes + ' (Sterling Heights Core)' },
                  { icon: 'Calendar', title: 'Next Delivery Date', text: 'October 15th, 2024 (Reservations Close Sept 30)' },
                  { icon: 'RefreshCw', title: 'Frequency', text: 'We mail every other month so your ad stays fresh and builds recognition.' },
                  { icon: 'CheckCircle', title: 'USPS Approved', text: 'Official EDDM Broker handling all sorting, logistics, and delivery.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-lg border-2 border-emerald-500 flex items-center justify-center shrink-0">
                      <SafeIcon name={item.icon} className="text-xl text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-display text-lg uppercase text-white">{item.title}</h4>
                      <p className="font-body text-slate-400 font-bold">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800 border-4 border-emerald-500 rounded-[2.5rem] p-10 text-center">
              <h3 className="font-display text-3xl uppercase mb-6">Our Quality Promise</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="border-2 border-slate-700 p-4 rounded-xl">
                  <div className="text-emerald-400 font-display text-2xl mb-1">100%</div>
                  <div className="font-body text-[10px] uppercase font-black text-slate-500">Print Guarantee</div>
                </div>
                <div className="border-2 border-slate-700 p-4 rounded-xl">
                  <div className="text-emerald-400 font-display text-2xl mb-1">FREE</div>
                  <div className="font-body text-[10px] uppercase font-black text-slate-500">Design Included</div>
                </div>
              </div>
              <div className="mt-8">
                <img 
                  src="https://images.unsplash.com/photo-1582159328034-ad01552661d2?auto=format&fit=crop&q=80" 
                  alt="Sterling Heights Street" 
                  className="w-full h-48 object-cover rounded-xl border-2 border-slate-700 mb-6 grayscale"
                />
                <RetroButton 
                  onClick={() => handleOpenModal({ label: 'Zip Map Request' })}
                  variant="primary" 
                  className="w-full !bg-orange-500 !text-white !border-white"
                >
                  Send me the Zip Code Map
                </RetroButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Legal */}
      <footer className="py-12 bg-white border-t-4 border-slate-200 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-body text-sm text-slate-400 font-bold mb-2 uppercase tracking-widest">
            © {new Date().getFullYear()} Mailbox Flyers • Sterling Heights Edition
          </p>
          <p className="font-body text-[10px] text-slate-300 uppercase tracking-tighter">
            MailboxFlyer.com is a marketing service. We are not affiliated with the United States Postal Service.
          </p>
        </div>
      </footer>

      {/* Reservation Modal */}
      {showModal && (
        <ReserveModal 
          slot={selectedSlot} 
          onClose={() => setShowModal(false)} 
          onSubmit={handlePledgeSubmit} 
        />
      )}
    </div>
  );
};

export default Sterling;